import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Avatar, Chip, IconButton, Menu, MenuItem, ListItemIcon,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Alert, Snackbar,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  Switch, FormControlLabel, Select, FormControl,
} from '@mui/material';
import {
  PersonAddRounded, SearchRounded, MoreVertRounded, EditRounded, BlockRounded,
  CheckCircleRounded, DeleteRounded, GroupsRounded,
} from '@mui/icons-material';
import { colors } from '../../theme/theme';
import { useTeam } from '../../hooks/useTeam';
import {
  TeamMember, AssignableTeamRole, ROLE_OPTIONS, roleChipColor, roleGradient,
  memberDisplayStatus, isMemberActive,
} from '../../api/teamTypes';
import { can, initials } from '../../auth/session';
import { COUNTRIES, DEFAULT_COUNTRY } from '../../constants/countries';

const card = { bgcolor: colors.backgroundPaper, border: `1px solid ${colors.border}`, borderRadius: '14px' };

const StatusChip: React.FC<{ status: TeamMember['status'] }> = ({ status }) => {
  const d = memberDisplayStatus(status);
  const map = {
    active: { bg: '#ECFDF5', fg: colors.success },
    invited: { bg: '#FFFBEB', fg: '#D97706' },
    deactivated: { bg: colors.gray100, fg: colors.gray600 },
  }[d.kind];
  return <Chip size="small" label={d.label} sx={{ bgcolor: map.bg, color: map.fg, fontWeight: 600, height: 24 }} />;
};

const TeamPage: React.FC = () => {
  const { listTeam, searchTeam, createMember, updateMember, toggleStatus, deleteMember } = useTeam();
  const canManage = can('can_manage_team');

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ el: HTMLElement; m: TeamMember } | null>(null);
  const [editTarget, setEditTarget] = useState<TeamMember | 'new' | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'toggle' | 'delete'; m: TeamMember } | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = search.trim() ? await searchTeam(search.trim()) : (await listTeam()).data;
      setMembers(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load team.');
    } finally { setLoading(false); }
  }, [search, listTeam, searchTeam]);

  useEffect(() => {
    const t = setTimeout(reload, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [reload, search]);

  const activeCount = useMemo(() => members.filter((m) => isMemberActive(m.status)).length, [members]);

  const runConfirm = async () => {
    if (!confirm) return;
    const { kind, m } = confirm; setConfirm(null);
    try {
      if (kind === 'toggle') { await toggleStatus(m.id); setToast(isMemberActive(m.status) ? 'Member deactivated' : 'Member reactivated'); }
      else { await deleteMember(m.id); setToast('Member removed'); }
      reload();
    } catch (e: any) { setToast(e?.response?.data?.message || 'Action failed'); }
  };

  return (
    <Box>
      {/* Header — all controls share a 40px height and are vertically centered */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ ...card, height: 40, px: 1.75, display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupsRounded sx={{ color: colors.primary, fontSize: 20 }} />
            <Typography sx={{ fontSize: 14 }}><b>{members.length}</b> Members</Typography>
          </Box>
          <Box sx={{ ...card, height: 40, px: 1.75, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleRounded sx={{ color: colors.success, fontSize: 20 }} />
            <Typography sx={{ fontSize: 14 }}><b>{activeCount}</b> Active</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField size="small" placeholder="Search members" value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }} sx={{ width: 230, '& .MuiOutlinedInput-root': { height: 40 } }} />
          {canManage && <Button variant="contained" disableElevation startIcon={<PersonAddRounded />} onClick={() => setEditTarget('new')} sx={{ height: 40, whiteSpace: 'nowrap' }}>Add member</Button>}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ ...card, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>
        ) : members.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h4" sx={{ mb: 0.5 }}>No members</Typography>
            <Typography color="text.secondary">{search ? 'No members match your search.' : 'Add your first team member.'}</Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow><TableCell>Member</TableCell><TableCell>Role</TableCell><TableCell>Status</TableCell><TableCell align="right" /></TableRow>
            </TableHead>
            <TableBody>
              {members.map((m) => {
                const name = m.user.display_name || m.user.phone_number || 'Member';
                const deactivated = memberDisplayStatus(m.status).kind === 'deactivated';
                const rc = roleChipColor(m.role_name);
                return (
                  <TableRow key={m.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 40, height: 40, fontSize: 14, background: roleGradient(m.role_name, deactivated) }}>{initials(name)}</Avatar>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography sx={{ fontWeight: 600 }}>{name}</Typography>
                            {m.is_creator && <Chip size="small" label="Creator" sx={{ height: 18, fontSize: 10, bgcolor: colors.gray100, color: colors.gray600 }} />}
                          </Box>
                          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>{m.user.country_code} {m.user.phone_number}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell><Chip size="small" label={m.role_display_name} sx={{ bgcolor: rc.bg, color: rc.fg, fontWeight: 600, height: 24 }} /></TableCell>
                    <TableCell><StatusChip status={m.status} /></TableCell>
                    <TableCell align="right">
                      {canManage && !m.is_creator && (
                        <IconButton size="small" onClick={(e) => setMenu({ el: e.currentTarget, m })}><MoreVertRounded /></IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>
      <Typography sx={{ fontSize: 13, color: colors.textSecondary, mt: 2 }}>
        Roles exist within this business. The creator is the Administrator and cannot be edited or removed.
      </Typography>

      {/* Row actions menu */}
      <Menu anchorEl={menu?.el} open={!!menu} onClose={() => setMenu(null)} transformOrigin={{ horizontal: 'right', vertical: 'top' }} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
        <MenuItem onClick={() => { const m = menu!.m; setMenu(null); setEditTarget(m); }}><ListItemIcon><EditRounded fontSize="small" /></ListItemIcon>Edit details</MenuItem>
        {menu && isMemberActive(menu.m.status) ? (
          <MenuItem onClick={() => { const m = menu!.m; setMenu(null); setConfirm({ kind: 'toggle', m }); }} sx={{ color: colors.error }}><ListItemIcon><BlockRounded fontSize="small" sx={{ color: colors.error }} /></ListItemIcon>Deactivate</MenuItem>
        ) : (
          <MenuItem onClick={() => { const m = menu!.m; setMenu(null); setConfirm({ kind: 'toggle', m }); }}><ListItemIcon><CheckCircleRounded fontSize="small" /></ListItemIcon>Reactivate</MenuItem>
        )}
        <MenuItem onClick={() => { const m = menu!.m; setMenu(null); setConfirm({ kind: 'delete', m }); }} sx={{ color: colors.error }}><ListItemIcon><DeleteRounded fontSize="small" sx={{ color: colors.error }} /></ListItemIcon>Remove</MenuItem>
      </Menu>

      {/* Add / Edit dialog */}
      {editTarget && (
        <MemberDialog
          member={editTarget === 'new' ? null : editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={(msg) => { setEditTarget(null); setToast(msg); reload(); }}
          createMember={createMember}
          updateMember={updateMember}
        />
      )}

      {/* Confirm dialog */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          {confirm?.kind === 'delete' ? 'Remove member?' : isMemberActive(confirm?.m.status ?? 'active') ? 'Deactivate member?' : 'Reactivate member?'}
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            {confirm?.kind === 'delete'
              ? `${confirm?.m.user.display_name || 'This member'} will be removed from the team.`
              : isMemberActive(confirm?.m.status ?? 'active')
              ? 'They will lose access to this business until reactivated.'
              : 'They will regain access to this business.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="contained" color={confirm?.kind === 'delete' || isMemberActive(confirm?.m.status ?? 'active') ? 'error' : 'primary'} onClick={runConfirm}>
            {confirm?.kind === 'delete' ? 'Remove' : isMemberActive(confirm?.m.status ?? 'active') ? 'Deactivate' : 'Reactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

/* ---------------- Add / Edit member dialog ---------------- */
const MemberDialog: React.FC<{
  member: TeamMember | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
  createMember: ReturnType<typeof useTeam>['createMember'];
  updateMember: ReturnType<typeof useTeam>['updateMember'];
}> = ({ member, onClose, onSaved, createMember, updateMember }) => {
  const isEdit = !!member;
  const [name, setName] = useState(member?.user.display_name || '');
  const [code, setCode] = useState(member?.user.country_code || DEFAULT_COUNTRY.dialCode);
  const [phone, setPhone] = useState(member?.user.phone_number || '');
  const [role, setRole] = useState<AssignableTeamRole>(
    (member && member.role_name !== 'administrator' ? member.role_name : 'provider') as AssignableTeamRole
  );
  const [isProvider, setIsProvider] = useState(member?.is_provider ?? false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!name.trim()) return setErr('Enter a name.');
    if (!isEdit && !phone.trim()) return setErr('Enter a phone number.');
    setSaving(true); setErr(null);
    try {
      if (isEdit) { await updateMember(member!.id, { display_name: name.trim(), role, is_provider: isProvider }); onSaved('Member updated'); }
      else { await createMember({ display_name: name.trim(), phone_number: phone.trim(), country_code: code, role, is_provider: isProvider }); onSaved(`${name.trim()} added to team`); }
    } catch (e: any) { setErr(e?.response?.data?.message || 'Failed to save member.'); setSaving(false); }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{isEdit ? 'Edit member' : 'Add team member'}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <TextField fullWidth label="Full name" value={name} onChange={(e) => setName(e.target.value)} sx={{ mt: 1, mb: 2 }} />
        {!isEdit && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <FormControl sx={{ width: 110 }}>
              <Select value={code} onChange={(e) => setCode(e.target.value)} size="small" sx={{ height: 56 }}>
                {COUNTRIES.map((c) => <MenuItem key={c.id} value={c.dialCode}>{c.flag} {c.dialCode}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Phone" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))} />
          </Box>
        )}
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Role</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {ROLE_OPTIONS.map((r) => {
            const sel = role === r.id;
            return (
              <Box key={r.id} onClick={() => setRole(r.id)} sx={{ p: 1.5, borderRadius: '12px', cursor: 'pointer', border: `1.5px solid ${sel ? colors.primary : colors.inputBorder}`, bgcolor: sel ? colors.primaryLight : 'transparent' }}>
                <Typography sx={{ fontWeight: 700 }}>{r.label}</Typography>
                <Typography sx={{ fontSize: 12.5, color: colors.textSecondary }}>{r.description}</Typography>
              </Box>
            );
          })}
        </Box>
        <FormControlLabel sx={{ mt: 1 }} control={<Switch checked={isProvider} onChange={(e) => setIsProvider(e.target.checked)} />} label="Can also serve as a provider" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving} onClick={save}>{saving ? <CircularProgress size={20} color="inherit" /> : isEdit ? 'Save' : 'Add member'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamPage;
